import { useContext } from 'react'
import { StyleSheet, View } from 'react-native'

import { buttonRadius } from '../theme/buttonRadius'
import { useTheme } from '../theme/restyle'
import { SPINNER_APPEAR_DELAY_MS } from '../utils/motion'
import { DepositActionLayers, DepositActionMotion } from './DepositActionMotion'
import { LoadingSpinner } from './LoadingSpinner'
import type { PillButtonSize } from './PillButton'

// The ring's own size in the action, from the design (2475:2864).
const SPINNER_SIZE = 32

/**
 * The action's own slot while the thing that fills it is still coming — a
 * quote, a provider's hosted button. Drawn as the button's shape in the quiet
 * secondary fill, with no label: the design gives the waiting control no copy,
 * so `label` is what a screen reader announces rather than anything drawn.
 *
 * Shares {@link PillButton}'s geometry and motion so the slot doesn't change
 * shape when the real action lands in it. The ring itself is held back for a
 * beat (`delay`) — the fill is up from the first frame,
 * so a wait that ends inside the delay leaves the button looking untouched.
 */
export function ActionLoading({
  label,
  size = 'buttonLg',
  spinnerDelay = SPINNER_APPEAR_DELAY_MS,
}: {
  label: string
  size?: PillButtonSize
  /** Milliseconds before the ring is drawn; the fill is up from the first frame. */
  spinnerDelay?: number
}) {
  const animateDeposit = useContext(DepositActionMotion)
  const theme = useTheme()
  const height = theme.sizes[size]
  const radius = buttonRadius(theme, height)
  const fill = theme.background.button.secondary.default

  // The lead arc is the fill's own content color; the track is the `loading
  // alpha` the token export defines for exactly this state.
  const spinner = (
    <LoadingSpinner
      size={SPINNER_SIZE}
      color={theme.content.button.secondary.default}
      trackColor={theme.content.button.secondary.loadingAlpha}
      delay={spinnerDelay}
    />
  )

  return (
    // `accessible` is what makes this a VoiceOver stop — a role and a label
    // alone leave it unfocusable on iOS, and the slot hides what it covers.
    <View
      accessible
      accessibilityRole="progressbar"
      accessibilityLabel={label}
      accessibilityState={{ busy: true }}
    >
      {animateDeposit ? (
        <DepositActionLayers
          stateKey={`loading:${size}`}
          style={{ height }}
          fillColor={fill}
          radius={radius}
          contentStyle={styles.animatedContent}
        >
          {spinner}
        </DepositActionLayers>
      ) : (
        <View
          style={[
            styles.center,
            { height, borderRadius: radius, backgroundColor: fill },
          ]}
        >
          {spinner}
        </View>
      )}
    </View>
  )
}

const styles = StyleSheet.create({
  center: { alignItems: 'center', justifyContent: 'center' },
  animatedContent: {
    flex: 1,
    alignItems: 'center',
    justifyContent: 'center',
  },
})
